import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Alert,
  Fade,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { AuthContext } from "../../../context/AuthContext/AuthContext";
import { isTokenExpired } from "../../../utils/services/Auth/isTokenExpired";
import LogoHeader from "../components/LogoHeader";
import LockoutCountdown from "../components/LockoutCountdown";
import RoleSelectorDialog from "../components/RoleSelectorDialog";
import LoginBack from "../../../assets/Images/LoginBack2.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn, switchRole } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [attemptInfo, setAttemptInfo] = useState(null); // { current, max }
  const [sessionMessage, setSessionMessage] = useState(null);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  // Mostrar mensaje de sesión si venimos redirigidos
  useEffect(() => {
    if (location.state?.message) {
      setSessionMessage({
        text: location.state.message,
        type: location.state.type || "info",
      });
    }
  }, [location.state]);

  // Redirigir si ya está autenticado, no necesita seleccionar rol, y no tiene cambio de contraseña pendiente
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (user && token && !isTokenExpired(token)) {
      if (user.forcePasswordChange || user.passwordExpired) {
        navigate("/auth/change-password", {
          state: {
            forced: true,
            reason: user.passwordExpired
              ? "Your password has expired (90+ days). You must change it before continuing."
              : "You must change your password before continuing.",
          },
        });
      } else if (!user.requireRoleSelection) {
        navigate("/Main");
      }
    }
  }, [user, navigate]);

  // Parsear info de intentos del mensaje "Intento X de Y"
  const parseAttemptInfo = useCallback((message) => {
    const match = message.match(/Intento (\d+) de (\d+)/i)
      || message.match(/Attempt (\d+) of (\d+)/i);
    if (match) {
      return { current: parseInt(match[1]), max: parseInt(match[2]) };
    }
    return null;
  }, []);

  const handleLogin = async () => {
    setError(null);
    setAttemptInfo(null);

    if (!email.includes("@")) {
      setError({ text: "Please enter a valid email address.", severity: "warning" });
      return;
    }
    if (password.length < 8) {
      setError({ text: "Password must be at least 8 characters.", severity: "warning" });
      return;
    }

    setLoading(true);
    try {
      const data = await signIn(email, password);

      // signIn retorna la data del backend + userData
      if (data.result === 0) {
        // Login exitoso - verificar si necesita cambiar contraseña
        if (data.forcePasswordChange || data.passwordExpired) {
          navigate("/auth/change-password", {
            state: {
              forced: true,
              reason: data.passwordExpired
                ? "Your password has expired (90+ days). You must change it before continuing."
                : "You must change your password before continuing.",
            },
          });
        } else if (data.userData?.requireRoleSelection) {
          // Múltiples roles → mostrar selector
          setAvailableRoles(data.userData.roles);
          setShowRoleSelector(true);
        } else {
          navigate("/Main");
        }
      }
    } catch (err) {
      const responseData = err?.response?.data || err;
      const result = responseData?.result;
      const message = responseData?.message || "An unexpected error occurred. Please try again.";

      switch (result) {
        case 1: // Correo o contraseña incorrectos (sin contador)
          setError({ text: "Invalid email or password.", severity: "error" });
          break;

        case 2: // Cuenta inactiva
          setError({
            text: "Your account is inactive. Please contact your administrator.",
            severity: "error",
          });
          break;

        case 3: // Cuenta bloqueada (temporal con fecha o permanente)
          setError({ text: message, severity: "error" });
          break;

        case 4: // Bloqueada por intentos
          setIsLocked(true);
          break;

        case 5: { // Correo o contraseña incorrectos con contador
          const info = parseAttemptInfo(message);
          setAttemptInfo(info);
          setError({ text: "Invalid email or password.", severity: "error" });
          break;
        }

        default:
          setError({ text: message, severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && email.includes("@") && password.length >= 8 && !loading) {
      handleLogin();
    }
  };

  const handleLockoutComplete = () => {
    setIsLocked(false);
    setError(null);
    setAttemptInfo(null);
  };

  const handleRoleSelected = async (roleId) => {
    await switchRole(roleId);
    setShowRoleSelector(false);
    navigate("/Main");
  };

  const isFormValid = email.includes("@") && password.length >= 8;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundImage: `url(${LoginBack})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Fade in timeout={600}>
        <Card
          sx={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 4,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          }}
        >
          {loading && <LinearProgress sx={{ height: 3 }} />}

          <CardContent sx={{ px: 4, py: 4 }}>
            <LogoHeader />

            {/* Estado de lockout */}
            {isLocked ? (
              <LockoutCountdown
                durationSeconds={1800}
                onComplete={handleLockoutComplete}
              />
            ) : (
              <>
                {/* Mensaje de sesión expirada */}
                {sessionMessage && (
                  <Fade in>
                    <Alert
                      severity={sessionMessage.type}
                      onClose={() => setSessionMessage(null)}
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      {sessionMessage.text}
                    </Alert>
                  </Fade>
                )}

                {/* Error de login */}
                {error && (
                  <Fade in>
                    <Alert severity={error.severity} sx={{ mb: 2, borderRadius: 2 }}>
                      {error.text}
                    </Alert>
                  </Fade>
                )}

                {/* Indicador de intentos */}
                {attemptInfo && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", mb: 0.5 }}>
                      {Array.from({ length: attemptInfo.max }, (_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: i < attemptInfo.current ? "#d32f2f" : "#e0e0e0",
                            transition: "background-color 0.3s ease",
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "#d32f2f" }}>
                      Attempt {attemptInfo.current} of {attemptInfo.max}
                    </Typography>
                  </Box>
                )}

                {/* Email */}
                <TextField
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="user@santeplusmedical.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                      "&:hover fieldset": { borderColor: "#00bcd4" },
                      "&.Mui-focused fieldset": { borderColor: "#0C343D" },
                    },
                  }}
                />

                {/* Password */}
                <TextField
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  placeholder="Enter your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                      "&:hover fieldset": { borderColor: "#00bcd4" },
                      "&.Mui-focused fieldset": { borderColor: "#0C343D" },
                    },
                  }}
                />

                {/* Login button */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleLogin}
                  disabled={!isFormValid || loading}
                  sx={{
                    mt: 3,
                    mb: 1,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    bgcolor: "#0C343D",
                    boxShadow: "0 4px 12px rgba(12,52,61,0.3)",
                    "&:hover": {
                      bgcolor: "#0a2a33",
                      boxShadow: "0 6px 16px rgba(12,52,61,0.4)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#ccc",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Sign In"
                  )}
                </Button>

                {/* Forgot password link - futuro */}
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    color: "#0C343D",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline", color: "#00bcd4" },
                    transition: "color 0.2s ease",
                  }}
                  onClick={() => {
                    // Futuro: navigate("/auth/forgot-password")
                  }}
                >
                  Forgot your password?
                </Typography>

                {/* HIPAA compliance note */}
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 3,
                    color: "#aaa",
                    lineHeight: 1.4,
                  }}
                >
                  This system contains protected health information (PHI).
                  <br />
                  Unauthorized access is strictly prohibited.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Role selector dialog for multi-role users */}
      <RoleSelectorDialog
        open={showRoleSelector}
        roles={availableRoles}
        onSelectRole={handleRoleSelected}
      />
    </Box>
  );
};

export default LoginPage;
